import { createTheme } from '@mui/material/styles';

// Shopify Polaris inspired theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#008060', // Shopify green
      light: '#00a884',
      dark: '#005c46',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#5C6AC4', // Shopify purple
      light: '#7c8dd4',
      dark: '#4c5aa4',
    },
    success: {
      main: '#008060',
      light: '#e3f9f2',
    },
    error: {
      main: '#D72C0D',
      light: '#fef1ed',
    },
    warning: {
      main: '#FFC453',
      light: '#fff6e6',
    },
    info: {
      main: '#006fbb',
      light: '#e0f2ff',
    },
    background: {
      default: '#F6F6F7',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#202223',
      secondary: '#6D7175',
    },
    divider: '#E1E3E5',
  },
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      'San Francisco',
      'Segoe UI',
      'Roboto',
      'Helvetica Neue',
      'sans-serif',
    ].join(','),
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
      color: '#202223',
      letterSpacing: '-0.02em',
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 600,
      color: '#202223',
      letterSpacing: '-0.01em',
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
      color: '#202223',
    },
    subtitle1: {
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#6D7175',
    },
    body1: {
      fontSize: '0.875rem',
      color: '#202223',
    },
    body2: {
      fontSize: '0.8125rem',
      color: '#6D7175',
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
      fontSize: '0.875rem',
    },
  },
  shape: {
    borderRadius: 8,
  },
  shadows: [
    'none',
    '0 1px 0 0 rgba(0,0,0,0.05)',
    '0 1px 3px 0 rgba(0,0,0,0.1), 0 1px 2px 0 rgba(0,0,0,0.06)',
    '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)',
    '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)',
    '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)',
    '0 25px 50px -12px rgba(0,0,0,0.25)',
    '0 25px 50px -12px rgba(0,0,0,0.25)',
    '0 25px 50px -12px rgba(0,0,0,0.25)',
    '0 25px 50px -12px rgba(0,0,0,0.25)',
    '0 25px 50px -12px rgba(0,0,0,0.25)',
    '0 25px 50px -12px rgba(0,0,0,0.25)',
    '0 25px 50px -12px rgba(0,0,0,0.25)',
    '0 25px 50px -12px rgba(0,0,0,0.25)',
    '0 25px 50px -12px rgba(0,0,0,0.25)',
    '0 25px 50px -12px rgba(0,0,0,0.25)',
    '0 25px 50px -12px rgba(0,0,0,0.25)',
    '0 25px 50px -12px rgba(0,0,0,0.25)',
    '0 25px 50px -12px rgba(0,0,0,0.25)',
    '0 25px 50px -12px rgba(0,0,0,0.25)',
    '0 25px 50px -12px rgba(0,0,0,0.25)',
    '0 25px 50px -12px rgba(0,0,0,0.25)',
    '0 25px 50px -12px rgba(0,0,0,0.25)',
    '0 25px 50px -12px rgba(0,0,0,0.25)',
    '0 25px 50px -12px rgba(0,0,0,0.25)',
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          padding: '8px 16px',
          fontWeight: 500,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        contained: {
          backgroundColor: '#008060',
          '&:hover': {
            backgroundColor: '#005c46',
          },
        },
        outlined: {
          borderColor: '#C9CCCF',
          color: '#202223',
          '&:hover': {
            borderColor: '#8C9196',
            backgroundColor: 'transparent',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: '0 0 0 1px rgba(63,63,68,0.05), 0 1px 3px 0 rgba(63,63,68,0.15)',
        },
        elevation1: {
          boxShadow: '0 0 0 1px rgba(63,63,68,0.05), 0 1px 3px 0 rgba(63,63,68,0.15)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 0 0 1px rgba(63,63,68,0.05), 0 1px 3px 0 rgba(63,63,68,0.15)',
          borderRadius: 12,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 500,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 6,
            '&:hover fieldset': {
              borderColor: '#8C9196',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#005c46',
              borderWidth: 2,
            },
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #E1E3E5',
        },
        head: {
          fontWeight: 600,
          color: '#202223',
          backgroundColor: '#FAFBFB',
        },
      },
    },
  },
});

export default theme;
